import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { AlertCircle, CheckCircle2, ExternalLink, ListChecks, Lock, RefreshCw } from "lucide-react";
import { AppLink } from "@/components/AppLink";
import { Skeleton } from "@/components/ui/skeleton";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { useStore } from "@/hooks/useStore";
import { structureProperty } from "@/endpoints/dryp/structureProperty";
import { Box, BoxHeader, BoxContent } from "./Box";

type StructurePropertiesProps = {
	structure: Dryp.Structure;
	onConfigured?: () => void;
};

export const StructureProperties = observer(({ structure, onConfigured }: StructurePropertiesProps) => {
	const structureId = structure.id as Dryp.StructureId;
	const propertyCountsApi = useAsyncStateStore(structureProperty.getCountsForStructure);
	const {
		uiStore: { modalStore },
	} = useStore();

	useEffect(() => {
		if (!structure.isActive) return;
		propertyCountsApi.fetch(structureId);
	}, [structureId, structure.isActive]);

	const handleConfigured = () => {
		propertyCountsApi.fetch(structureId);
		onConfigured?.();
	};

	const showSkeleton = propertyCountsApi.loading && !propertyCountsApi.loaded;
	const error = propertyCountsApi.error as ApiError | null;
	const counts = propertyCountsApi.data;
	const hasMissingForFlow = (counts?.missingForConnectivityCount ?? 0) > 0;

	return (
		<Box>
			<BoxHeader
				icon={<ListChecks className="w-4 h-4" />}
				title="Properties"
				action={
					structure.isActive ? (
						<AppLink
							to="Dashboard.Structures.Properties"
							params={{ id: structureId }}
							className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
						>
							See all properties
							<ExternalLink className="w-3 h-3" />
						</AppLink>
					) : undefined
				}
			/>
			<BoxContent>
				<div className="space-y-2">
					{!structure.isActive ? (
						<div className="flex flex-col items-center gap-1.5 rounded-md bg-muted/40 px-3 py-5 text-center">
							<Lock className="h-5 w-5 text-muted-foreground" />
							<p className="text-xs font-medium">Structure deactivated</p>
							<p className="text-[11px] text-muted-foreground">
								Properties can&apos;t be changed while it&apos;s deactivated.
							</p>
						</div>
					) : showSkeleton ? (
						<div className="space-y-2">
							<Skeleton className="h-9 w-full" />
							<Skeleton className="h-9 w-full" />
						</div>
					) : error ? (
						<div className="flex items-center justify-between gap-2 rounded-md border border-destructive/20 bg-destructive/10 p-3 text-xs text-destructive">
							<span className="flex items-center gap-2">
								<AlertCircle className="h-3.5 w-3.5 shrink-0" />
								{error.message || "Couldn't load properties."}
							</span>
							<button
								type="button"
								onClick={() => propertyCountsApi.refetch()}
								className="inline-flex items-center gap-1 text-[11px] font-medium underline-offset-2 hover:underline"
							>
								<RefreshCw className="h-3 w-3" />
								Retry
							</button>
						</div>
					) : counts ? (
						<div className="space-y-2">
							<div className="flex items-center gap-2 rounded-md bg-muted/40 px-3 py-2 text-xs">
								<CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
								<span className="font-medium tabular-nums">{counts.configuredCount}</span>
								<span className="text-muted-foreground">configured</span>
							</div>
							{hasMissingForFlow && (
								<button
									type="button"
									onClick={() =>
										modalStore.open("structurePropertiesWizard", {
											structureId,
											onClose: handleConfigured,
										})
									}
									className="flex w-full items-center gap-2 rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-600 transition-colors hover:bg-amber-500/20"
								>
									<AlertCircle className="h-3.5 w-3.5 shrink-0" />
									<span className="font-medium tabular-nums">
										{counts.missingForConnectivityCount}
									</span>
									<span>needed for I&amp;I</span>
									<span className="ml-auto font-medium text-amber-700">Configure &rarr;</span>
								</button>
							)}
						</div>
					) : null}
				</div>
			</BoxContent>
		</Box>
	);
});
